import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Image as ImageIcon, FileText, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import { useLang, pick } from "@/contexts/LanguageContext";
import { carDetailPath } from "@/i18n/routeMap";
import ImageModal from "@/components/cars/ImageModal";
import ConditionsDrawer from "@/components/cars/ConditionsDrawer";
import BookingModal from "@/components/cars/BookingModal";
import { localizeVehicleFact } from "@/lib/vehicleFacts";

export default function VehicleCard({ vehicle, settings }) {
  const { lang, t } = useLang();
  const [showImages, setShowImages] = useState(false);
  const [showConditions, setShowConditions] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const desc = pick(vehicle.descriptionShort, lang);
  const catLabel = pick(vehicle.categoryLabel, lang) || vehicle.categoryKey;

  // Normalise images: array of {url, ...} or legacy array of strings.
  const imageList = (vehicle.images || []).map((i) => (typeof i === "string" ? { url: i, isMain: false, order: 0 } : i));
  const sorted = [...imageList].sort((a, b) => (a.order || 0) - (b.order || 0));
  const main = sorted.find((i) => i.isMain) || sorted[0];
  const mainUrl = main?.url || "";
  const allUrls = sorted.map((i) => i.url).filter(Boolean);
  const detailPath = vehicle.slug ? carDetailPath(vehicle.slug, lang) : null;

  return (
    <article
      className="group bg-[#0C0C0C] border border-[#2A2A2A] hover:border-[#C9A55A] transition-colors duration-300 flex flex-col"
      data-testid={`vehicle-card-${vehicle.id}`}>
      {detailPath ? (
        <Link
          to={detailPath}
          className="relative aspect-[4/3] overflow-hidden bg-[#2A2A2A] block w-full"
          data-testid={`vehicle-image-${vehicle.id}`}>
          <img src={mainUrl} alt={vehicle.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute top-3 left-3 px-2.5 h-7 inline-flex items-center text-[10px] tracking-[0.18em] uppercase font-semibold bg-[#0C0C0C]/80 text-[#C9A55A] border border-[#C9A55A]/40">
            {catLabel}
          </div>
          {!vehicle.available && (
            <div className="absolute inset-0 bg-[#0C0C0C]/70 flex items-center justify-center">
              <span className="text-overline text-[#B8342A] border border-[#B8342A] px-4 py-2">{t.catalog.notAvailable}</span>
            </div>
          )}
        </Link>
      ) : (
        <button
          onClick={() => setShowImages(true)}
          className="relative aspect-[4/3] overflow-hidden bg-[#2A2A2A] block w-full"
          data-testid={`vehicle-image-${vehicle.id}`}>
          <img src={mainUrl} alt={vehicle.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute top-3 left-3 px-2.5 h-7 inline-flex items-center text-[10px] tracking-[0.18em] uppercase font-semibold bg-[#0C0C0C]/80 text-[#C9A55A] border border-[#C9A55A]/40">
            {catLabel}
          </div>
        </button>
      )}

      <div className="p-5 md:p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-[#F5F1EB] text-xl md:text-2xl tracking-tight">{vehicle.name}</h3>
            {desc && <p className="mt-2 text-sm text-[#8A8A8A] font-light leading-relaxed line-clamp-2">{desc}</p>}
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase">{t.catalog.from}</div>
            {vehicle.priceMonthly && Number(vehicle.priceMonthly) > 0 ? (
              <>
                <div className="font-display text-[#C9A55A] text-2xl md:text-3xl">€{Math.round(vehicle.priceMonthly)}</div>
              </>
            ) : (
              <div className="font-display text-[#C9A55A] text-base md:text-lg leading-tight">{t.catalog.priceOnRequest}</div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#8A8A8A] uppercase tracking-[0.12em] mt-4">
          {vehicle.horsepower ? <span>{vehicle.horsepower} {t.catalog.hp}</span> : null}
          {vehicle.topSpeed ? <span>· {t.catalog.topSpeed} {vehicle.topSpeed} KM/H</span> : null}
          {vehicle.acceleration ? <span>· {t.catalog.acceleration} {vehicle.acceleration}s</span> : null}
          {vehicle.transmission ? <span>· {localizeVehicleFact(vehicle.transmission, lang)}</span> : null}
          {vehicle.seats ? <span>· {vehicle.seats} {t.catalog.seats}</span> : null}
        </div>

        {vehicle.location && (
          <div className="flex items-center gap-1.5 text-[11px] text-[#C9A55A] uppercase tracking-[0.14em] mt-2">
            <MapPin size={11} /> {vehicle.location}
          </div>
        )}

        <div className="flex-1" />

        <div className="grid grid-cols-2 gap-2 mt-6">
          <button onClick={() => setShowImages(true)}
            className="border border-[#2A2A2A] hover:border-[#C9A55A] hover:text-[#C9A55A] text-[#F5F1EB] text-xs tracking-[0.16em] uppercase font-semibold py-3 transition-colors flex items-center justify-center gap-2"
            data-testid={`view-images-${vehicle.id}`}>
            <ImageIcon size={14} /> {t.catalog.viewImages}
          </button>
          <button onClick={() => setShowConditions(true)}
            className="border border-[#2A2A2A] hover:border-[#C9A55A] hover:text-[#C9A55A] text-[#F5F1EB] text-xs tracking-[0.16em] uppercase font-semibold py-3 transition-colors flex items-center justify-center gap-2"
            data-testid={`conditions-${vehicle.id}`}>
            <FileText size={14} /> {t.catalog.conditions}
          </button>
        </div>

        {detailPath && (
          <Link
            to={detailPath}
            data-testid={`more-info-${vehicle.id}`}
            className="mt-3 text-center text-[11px] tracking-[0.18em] uppercase text-[#C9A55A] hover:text-[#F5F1EB] inline-flex items-center justify-center gap-1.5">
            {t.catalog.moreInfo} <ArrowRight size={12} />
          </Link>
        )}

        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => setBookingOpen(true)}
          className="btn-red w-full mt-2"
          data-testid={`reserve-${vehicle.id}`}>
          <MessageCircle size={16} />
          {t.catalog.reserve}
        </motion.button>
      </div>

      <ImageModal open={showImages} onClose={() => setShowImages(false)} images={allUrls.length ? allUrls : [mainUrl]} title={vehicle.name} />
      <ConditionsDrawer open={showConditions} onClose={() => setShowConditions(false)} vehicle={vehicle} settings={settings} />
      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} vehicle={vehicle} settings={settings} />
    </article>
  );
}
